// Path: frontend/src/lib/api/editor.ts
import { api, getToken } from './core';
import type { 
  SlideDetail, ChatMessage, ChatResponse, CloudResource, 
  OSSUploadResponse, OSSDeleteResponse, SysPrompt, 
  InlinePrompt, SlideGuideMessage 
} from './core';
// ==========================================
// 🎨 Cover Image Generation
// ==========================================
export interface PromptResponse {
  status: 'success';
  prompt: string;
  from_cache: boolean;
  input_tokens?: number;
  output_tokens?: number;
  input_money?: number;
  output_money?: number;
}

export interface PromptErrorResponse {
  type: 'error';
  content: string;
}

const getSysPromptIdByCourseId = (courseId: number | undefined): number | undefined => {
  if (courseId === 5) return 6;
  if (courseId === 6) return 18;
  return undefined;
};

export const generateCoverPrompt = (lessonId: number, lessonTitle: string, learningObjectives?: string, courseId: number = 5) => {
  const promptId = getSysPromptIdByCourseId(courseId);
  return api.post<PromptResponse>(`/courses/lessons/${lessonId}/gen-cover-prompt`, { 
    lesson_title: lessonTitle, 
    learning_objectives: learningObjectives, 
    course_id: courseId,
    ...(promptId && { prompt_id: promptId })
  }, { timeout: 600000 });
};

export const generateCoverImage = (lessonId: number, prompt: string) => 
  api.post<{ image_url: string }>(`/courses/lessons/${lessonId}/gen-cover-img`, { prompt: prompt }, { timeout: 600000 });

// ==========================================
// 🖼️ Background Image Generation
// ==========================================
export const generateBackgroundPrompt = (lessonId: number, lessonTitle: string, learningObjectives?: string, courseId: number = 5) => {
  const promptId = getSysPromptIdByCourseId(courseId);
  return api.post<PromptResponse>(`/courses/lessons/${lessonId}/gen-background-prompt`, { 
    lesson_title: lessonTitle, 
    learning_objectives: learningObjectives, 
    course_id: courseId,
    ...(promptId && { prompt_id: promptId })
  }, { timeout: 600000 });
};

export const generateBackgroundImage = (lessonId: number, prompt: string) => 
  api.post<{ image_url: string }>(`/courses/lessons/${lessonId}/gen-background-img`, { prompt: prompt }, { timeout: 600000 });

// ==========================================
// 🗑️ Clear Prompt
// ==========================================
export const clearPrompt = (lessonId: number, promptType: 'cover' | 'background') => 
  api.post<{ status: string; message: string }>(`/courses/lessons/${lessonId}/clear-prompt`, { prompt_type: promptType });

// ==========================================
// 📤 File Uploads
// ==========================================
export const uploadLessonCover = (lessonId: number, imageUrl: string) => api.post(`/courses/lessons/${lessonId}/cover/`, { image_url: imageUrl });
export const uploadLessonBackground = (lessonId: number, imageUrl: string) => api.post(`/courses/lessons/${lessonId}/background/`, { image_url: imageUrl });
export const importSyllabus = (file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/courses/import/', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};

export const importSyllabusByTitle = (courseId: number, file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/courses/${courseId}/import/by-title/`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};

export const importSyllabusBySortOrder = (courseId: number, file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/courses/${courseId}/import/by-sort-order/`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const uploadToOSS = (file: File, artifactId?: number) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post<OSSUploadResponse>('/upload/oss', formData, { headers: { 'Content-Type': 'multipart/form-data' }, params: artifactId ? { artifact_id: artifactId } : undefined });
};
export const deleteOSSFile = (resourceId: number) => api.delete<OSSDeleteResponse>(`/upload/oss/${resourceId}`);

// ==========================================
// 📄 Slide Development API (Gamified Editor)
// ==========================================
export const getSlideDetail = (slideId: number) => api.get<SlideDetail>(`/slides/${slideId}/`);
export const getSlideChatHistory = (slideId: number) => api.get<ChatMessage[]>(`/slides/${slideId}/chat-history/`);
export const getAdjacentSlide = (slideId: number, direction: 'prev' | 'next') => api.get<SlideDetail>(`/slides/${slideId}/adjacent`, { params: { direction } });
export const chatWithAI = (slideId: number, data: { content: string; sys_prompt_id: number }) => api.post<ChatResponse>(`/slides/${slideId}/chat`, data);
export const saveSlideHtml = (slideId: number, data: { html_code: string; side_guide: string; side_guide_content?: string; is_final: boolean; memory?: string }) => api.post<SlideDetail>(`/slides/${slideId}/save-html`, data);
export const deleteSlideChatMessage = (messageId: number) => api.delete<{ deleted_ids: number[] }>(`/slides/chat/message/${messageId}`);
export const deleteSingleSlideChatMessage = (messageId: number) => api.delete<{ deleted_id: number }>(`/slides/chat/message/${messageId}/single`);
export const deleteSingleChatMessage = (mode: string, messageId: number) => api.delete<{ deleted_id: number; status: string }>(`/workshop/chat/single/${mode}/${messageId}`);
export const clearSlideChatHistory = (slideId: number) => api.delete<{ status: string; message: string }>(`/slides/${slideId}/chat-history/`);
export const getSlideCloudResources = (slideId: number) => api.get<CloudResource[]>(`/slides/${slideId}/cloud-resources/`);
export const getSlideContext = (slideId: number) => api.get(`/slides/${slideId}/context/`);
export const clearSlideFields = (slideId: number) => api.post<{ status: string; message: string; slide_id: number; cleared_fields: string[] }>(`/slides/${slideId}/clear-fields`);

// ==========================================
// 🔍 Slide Audit
// ==========================================
export interface SlideAuditResponse {
  slide_id: number;
  slide_index: number;
  status: string;
  message: string;
  audit_content: string;
  input_tokens: number;
  output_tokens: number;
  input_money: number;
  output_money: number;
}

export const auditSlide = (slideId: number, promptId?: number, model?: string) => 
  api.post<SlideAuditResponse>(`/slides-audit/${slideId}/audit`, { 
    ...(promptId && { prompt_id: promptId }),
    ...(model && { model }) 
  });

// ==========================================
// 🧠 Prompts Management
// ==========================================
export const getSysPrompts = (category?: string) => api.get<SysPrompt[]>('/sys-prompts/', category ? { params: { category } } : undefined);
export const getSysPrompt = (id: number) => api.get<SysPrompt>(`/sys-prompts/${id}/`);
export const createSysPrompt = (name: string, category: string) => api.post('/sys-prompts/', { name, category, prompt_content: '' });
export const updateSysPrompt = (id: number, data: { name?: string, category?: string, prompt_content?: string }) => api.put(`/sys-prompts/${id}/`, data);
export const deleteSysPrompt = (id: number) => api.delete(`/sys-prompts/${id}/`);

export const getInlinePrompts = (sysPromptId: number) => api.get<InlinePrompt[]>(`/sys-prompts/${sysPromptId}/inline-prompts/`);
export const getInlinePromptDetail = (inlinePromptId: number) => api.get<InlinePrompt>(`/sys-prompts/inline-prompts/${inlinePromptId}/`);
export const updateInlinePrompt = (inlinePromptId: number, data: Partial<InlinePrompt>) => api.put<InlinePrompt>(`/sys-prompts/inline-prompts/${inlinePromptId}/`, data);
export const createInlinePrompt = (sysPromptId: number, data: { step: string; content: string }) => api.post<InlinePrompt>(`/sys-prompts/${sysPromptId}/inline-prompts/`, data);
export const deleteInlinePrompt = (inlinePromptId: number) => api.delete<{ status: string; message: string }>(`/sys-prompts/inline-prompts/${inlinePromptId}/`);

export type BatchUploadResult = {
  filename: string;
  step: string;
  status: 'success' | 'not_found' | 'error';
  message: string;
};

export type BatchUploadResponse = {
  status: string;
  total_files: number;
  results: BatchUploadResult[];
};

export const batchUploadInlinePrompts = (sysPromptId: number, files: File[]) => {
  const formData = new FormData();
  files.forEach(file => {
    formData.append('files', file);
  });
  return api.post<BatchUploadResponse>(`/sys-prompts/${sysPromptId}/inline-prompts/batch-upload/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

// ==========================================
// 💬 Stream & WebSockets
// ==========================================
export const streamChat = async (
  payload: { lesson_id: number; sys_prompt_id: number; message: string; history: any[] },
  callbacks: {
    onChunk: (text: string) => void;
    onReasoning?: (text: string) => void;
    onProgress?: (text: string) => void;
    onMeta?: (meta: any) => void;
    onError?: (text: string) => void;
    onDone: () => void;
  }
) => {
  try {
    const token = getToken();
    const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api/v1'}/workshop/chat/stream/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token && { 'Authorization': `Bearer ${token}` }) },
      body: JSON.stringify(payload),
    });
    const reader = response.body?.getReader();
    const decoder = new TextDecoder();
    if (!reader) return;
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      const chunk = decoder.decode(value);
      const lines = chunk.split('\n\n');
      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const dataStr = line.slice(6);
          if (dataStr === '[DONE]') { callbacks.onDone(); return; }
          try {
            const data = JSON.parse(dataStr);
            // 按 packet type 分发
            if (data.type === 'content' || (data.content && !data.type)) {
              callbacks.onChunk(data.content);
            } else if (data.type === 'reasoning') {
              callbacks.onReasoning?.(data.content || '');
            } else if (data.type === 'progress') {
              callbacks.onProgress?.(data.content || '');
            } else if (data.type === 'meta') {
              callbacks.onMeta?.(data);
            } else if (data.type === 'error') {
              callbacks.onError?.(data.content || '');
            }
          } catch (e) { console.warn("Stream parse error:", e); }
        }
      }
    }
  } catch (err) { console.error("Stream connection failed:", err); }
};

const setupWebSocket = (wsUrl: string, onMessage: (msg: SlideGuideMessage) => void) => {
  const ws = new WebSocket(wsUrl);
  ws.onmessage = (event) => {
    try { onMessage(JSON.parse(event.data)); } 
    catch (error) { onMessage({ type: 'error', content: 'Failed to parse message' }); }
  };
  ws.onerror = () => onMessage({ type: 'error', content: 'WebSocket connection error' });
  ws.onclose = (e) => { if (!e.wasClean) onMessage({ type: 'error', content: 'WebSocket closed unexpectedly' }); };
  return ws;
};

const getWsHost = () => {
  let host = window.location.host;
  if (host.includes('localhost') || host.includes('127.0.0.1')) {
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api/v1';
    if (apiBaseUrl.startsWith('/')) {
      // 相对路径，使用当前 host（通过 Vite 代理）
      // host 保持不变
    } else {
      try {
        const url = new URL(apiBaseUrl);
        host = `${url.hostname}:${url.port || (url.protocol === 'https:' ? '443' : '80')}`;
      } catch (e) { /* fallback */ }
    }
  }
  return host;
};

export const generateSlideGuides = (planId: number, startIndex: number = 0, onMessage: (message: SlideGuideMessage) => void) => {
  const token = getToken();
  if (!token) throw new Error('Authentication token not found');
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const wsUrl = `${protocol}//${getWsHost()}/api/v1/slides/plans/${planId}/slides/generate/guide?start_index=${startIndex}&token=${token}`;
  const ws = setupWebSocket(wsUrl, onMessage);

  return {
    start: (slideCount: number, startIdx: number = 0) => ws.send(JSON.stringify({ type: 'start', slide_count: slideCount, start_index: startIdx })),
    stop: () => ws.send(JSON.stringify({ type: 'stop' })),
    close: () =>    ws.close(),
    getReadyState: () => ws.readyState,
    READY_STATE: {
      CONNECTING: WebSocket.CONNECTING,
      OPEN: WebSocket.OPEN,
      CLOSING: WebSocket.CLOSING,
      CLOSED: WebSocket.CLOSED
    }
  };
};

// ==========================================
// 🎨 Slides Pipeline (课件优化流水线)
// ==========================================
export interface SlidesPipelineStartMessage {
  prompt: string;
  file_urls?: string[];
  start_phase_index?: number;
  start_step_index?: number;
}

export interface SlidesPipelineProgressMessage {
  type: 'start' | 'progress' | 'completed' | 'error';
  message?: string;
  total_steps?: number;
  processed_steps?: number;
  start_phase_index?: number;
  phase?: string;
  phase_index?: number;
  step?: string;
  step_index?: number;
  prompt_type?: 'sys_prompt' | 'inline_prompt';
  prompt_name?: string;
  status?: 'processing' | 'success' | 'error';
}

export interface SlidesPhaseStepOption {
  value: number;
  label: string;
  step_key?: string;
  description?: string;
}

export interface SlidesPhaseOption {
  value: number;
  label: string;
  description: string;
  start_step_index_options: SlidesPhaseStepOption[];
}

export interface SlidesPipelineOptions {
  start_phase_index_options: SlidesPhaseOption[];
}

export const SLIDES_PIPELINE_OPTIONS: SlidesPipelineOptions = {
  start_phase_index_options: [
    {
      value: 0,
      label: "阶段一：50页课件蓝图生成",
      description: "基于 1万字教案，抽离出 50 页的高精制导地图，包含宏观锚点、微观动作锁与过渡钩子。",
      start_step_index_options: [
        { value: 0, label: "全局大纲切片器", step_key: "Step1", description: "精准将原教案切分成 50 页 Markdown 表格，预埋伏笔与悬念" }
      ]
    },
    {
      value: 1,
      label: "阶段二：第1页(封面)极其克制开发",
      description: "元数据封面提取，极度克制的亲和力开场，防剧情提前泄露。",
      start_step_index_options: [
        { value: 0, label: "封面开发规范", step_key: "first_page_prompt", description: "强制读取【一、项目基础信息】，提取标题目标，只做亲和力引出，绝不加戏" }
      ]
    },
    {
      value: 2,
      label: "阶段三：第50页(最后一页)收尾开发",
      description: "课程收尾与总结，完成最后一页的完美落幕。",
      start_step_index_options: [
        { value: 0, label: "最后一页开发规范", step_key: "last_page_prompt", description: "课程收尾总结，回顾核心知识点，完美落幕" }
      ]
    },
    {
      value: 3,
      label: "阶段四：第2页(视频)原著保真与提取",
      description: "100% 忠实提取原教案分镜，自动补充 Midjourney 垫图锚点，生成无缝双重缓冲播放器。",
      start_step_index_options: [
        { value: 0, label: "视频分镜设计器", step_key: "P02", description: "剥夺编剧权限，精准提取 6 分镜剧本，结合主角垫图进行 Prompt 结构化润色" }
      ]
    },
    {
      value: 4,
      label: "阶段五：第N页(常规交互)极客体验设计与开发",
      description: "利用 12位专家矩阵构思「反 PPT 化」互动，强制物理 DOM 绑定、拖拽物理引擎、视觉资产复用。",
      start_step_index_options: [
        { value: 0, label: "多维专家交互设计", step_key: "P_sj", description: "遵循微观动作防越界死锁，查阅跨页视觉资产库，设计极致 Juiciness 多巴胺互动" },
        { value: 1, label: "前沿逻辑代码实现", step_key: "P_sj_2", description: "手写 pointerdown 物理引擎，数值老虎机翻滚，禁止脱节绝对定位，严格分层隔离" }
      ]
    },
    {
      value: 5,
      label: "阶段六：逐字稿教案与记忆留存",
      description: "强力承上启下，执行反低幼化教师极客语言，并在最后拦截单页状态存入数据库。",
      start_step_index_options: [
        { value: 0, label: "页边教案引擎", step_key: "side", description: "接收过渡钩子作为第一句话，最终必须输出【单页教研记忆快照】(含视觉资产)" }
      ]
    }
  ]
};

export const runSlidesPipeline = (
  onMessage: (message: SlidesPipelineProgressMessage) => void,
  onOpen?: () => void
) => {
  const token = getToken();
  if (!token) throw new Error('Authentication token not found');

  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const wsUrl = `${protocol}//${getWsHost()}/api/v1/slides/pipeline/evolve?token=${token}`;
  
  const ws = new WebSocket(wsUrl);
  
  ws.onopen = () => {
    console.log('🎨 [SlidesPipeline] WebSocket connected');
    if (onOpen) onOpen();
  };
  
  ws.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data);
      onMessage(data);
    } catch (error) {
      onMessage({ type: 'error', message: 'Failed to parse message' });
    }
  };
  
  ws.onerror = (e) => {
    console.error('🎨 [SlidesPipeline] WebSocket error:', e);
    onMessage({ type: 'error', message: 'WebSocket connection error' });
  };
  
  ws.onclose = (e) => {
    console.log('🎨 [SlidesPipeline] WebSocket closed:', e.code, e.reason);
    if (!e.wasClean && e.code !== 1000) {
      onMessage({ type: 'error', message: `Connection closed: ${e.reason || 'Unknown reason'}` });
    }
  };

  return {
    start: (prompt: string, fileUrls?: string[], startPhaseIndex?: number, startStepIndex?: number) => {
      const msg: SlidesPipelineStartMessage = { prompt };
      if (fileUrls && fileUrls.length > 0) {
        msg.file_urls = fileUrls;
      }
      if (startPhaseIndex !== undefined) {
        msg.start_phase_index = startPhaseIndex;
      }
      if (startStepIndex !== undefined) {
        msg.start_step_index = startStepIndex;
      }
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify(msg));
      } else {
        console.error('🎨 [SlidesPipeline] WebSocket not open, readyState:', ws.readyState);
      }
    },
    close: () => ws.close(),
    getReadyState: () => ws.readyState,
    READY_STATE: {
      CONNECTING: WebSocket.CONNECTING,
      OPEN: WebSocket.OPEN,
      CLOSING: WebSocket.CLOSING,
      CLOSED: WebSocket.CLOSED
    }
  };
};

export const generateSlides = (planId: number, slideCount: number, startIndex: number = 0, onMessage: (message: SlideGuideMessage) => void) => {
  const token = getToken();
  if (!token) throw new Error('Authentication token not found');

  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const wsUrl = `${protocol}//${getWsHost()}/api/v1/slides/plans/${planId}/slides/generate?token=${token}&slide_count=${slideCount}&start_index=${startIndex}`;
  const ws = setupWebSocket(wsUrl, onMessage);

  return {
    start: (sysPromptId: number, count: number, startIdx: number = 0) => {
      ws.send(JSON.stringify({ type: 'start', slide_count: count, sys_prompt_id: sysPromptId, start_index: startIdx }));
    },
    stop: () => ws.send(JSON.stringify({ type: 'stop' })),
    close: () => ws.close(),
    getReadyState: () => ws.readyState,
    READY_STATE: {
      CONNECTING: WebSocket.CONNECTING,
      OPEN: WebSocket.OPEN,
      CLOSING: WebSocket.CLOSING,
      CLOSED: WebSocket.CLOSED
    }
  };
};

export interface BatchSideGuideStartMessage {
  start_from_index?: number;
}

export interface InvalidSlide {
  slide_id: number;
  slide_index: number;
  version?: string;
  is_final: boolean;
  reason: string;
  html_preview: string | null;
}

export interface CheckInvalidHtmlResponse {
  plan_id: number;
  total_slides: number;
  valid_count: number;
  invalid_count: number;
  invalid_slides: InvalidSlide[];
}

export const checkInvalidHtml = (planId: number) => 
  api.get<CheckInvalidHtmlResponse>(`/slides/plans/${planId}/slides/check-invalid-html`);

export interface BatchSideGuideMessage {
  type: 'start' | 'progress' | 'slide_start' | 'slide_done' | 'completed' | 'error';
  plan_id?: number;
  total_slides?: number;
  skipped_count?: number;
  start_from_index?: number;
  success_count?: number;
  error_count?: number;
  current_slide_index?: number;
  message?: string;
  results?: any[];
  content?: string;
}

export const batchGenerateSideGuides = (planId: number, onMessage: (message: BatchSideGuideMessage) => void, onOpen?: () => void) => {
  const token = getToken();
  if (!token) throw new Error('Authentication token not found');

  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const wsUrl = `${protocol}//${getWsHost()}/api/v1/slides/plans/${planId}/slides/batch-generate-side-guides?token=${token}`;
  
  const ws = new WebSocket(wsUrl);
  
  ws.onopen = () => {
    console.log('🔌 [BatchSideGuide] WebSocket connected');
    if (onOpen) onOpen();
  };
  
  ws.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data);
      onMessage(data);
    } catch (error) {
      onMessage({ type: 'error', content: 'Failed to parse message' });
    }
  };
  
  ws.onerror = (e) => {
    console.error('🔌 [BatchSideGuide] WebSocket error:', e);
    onMessage({ type: 'error', content: 'WebSocket connection error' });
  };
  
  ws.onclose = (e) => {
    console.log('🔌 [BatchSideGuide] WebSocket closed:', e.code, e.reason);
    if (!e.wasClean) {
      onMessage({ type: 'error', content: 'WebSocket closed unexpectedly' });
    }
  };

  return {
    start: (startFromIndex?: number) => {
      const msg: BatchSideGuideStartMessage = {};
      if (startFromIndex !== undefined && startFromIndex !== null) {
        msg.start_from_index = startFromIndex;
      }
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify(msg));
      } else {
        console.error('🔌 [BatchSideGuide] WebSocket not open, readyState:', ws.readyState);
      }
    },
    close: () => ws.close(),
    getReadyState: () => ws.readyState,
    READY_STATE: {
      CONNECTING: WebSocket.CONNECTING,
      OPEN: WebSocket.OPEN,
      CLOSING: WebSocket.CLOSING,
      CLOSED: WebSocket.CLOSED
    }
  };
};

// ==========================================
// 🚀 Curriculum Pipeline (教案开发流水线)
// ==========================================
export interface CurriculumPipelineStartMessage {
  prompt: string;
  file_urls?: string[];
  start_phase_index?: number;
  start_loop_x?: number;
  start_step_index?: number;
}

export interface CurriculumPipelineProgressMessage {
  type: 'start' | 'progress' | 'completed' | 'error';
  message?: string;
  total_steps?: number;
  processed_steps?: number;
  phase?: string;
  phase_index?: number;
  loop_x?: number;
  step?: string;
  step_index?: number;
  prompt_type?: 'sys_prompt' | 'inline_prompt';
  prompt_name?: string;
  status?: 'processing' | 'success' | 'error';
}

export interface PhaseStepOption {
  value: number;
  label: string;
  description?: string;
}

export interface LoopXOption {
  value: number;
  label: string;
  cn_num?: string;
}

export interface PhaseOption {
  value: number;
  label: string;
  description: string;
  is_loop?: boolean;
  loop_range?: [number, number];
  start_loop_x_options?: LoopXOption[];
  start_step_index_options: PhaseStepOption[];
}

export interface PipelineOptions {
  start_phase_index_options: PhaseOption[];
}

export const PIPELINE_OPTIONS: PipelineOptions = {
  start_phase_index_options: [
    {
      value: 0,
      label: "阶段一：课程蓝图",
      description: "确立本课核心教学目标与痛点，排查全域知识库，防止内容超纲与形式重复。",
      start_step_index_options: [
        { value: 0, label: "启动开发", description: "锁定课程标题，建立初始叙事危机。" }
      ]
    },
    {
      value: 1,
      label: "阶段二：整体方案设计",
      description: "完成「从危机到解法」的宏观闭环，确立贯穿全课的实战项目及世界观设定。",
      start_step_index_options: [
        { value: 0, label: "初版生成", description: "设计核心痛点危机、场景世界观和贯穿全课的实战项目。" },
        { value: 1, label: "蓝军打分", description: "对生成的整体方案进行逻辑挑刺与防超纲验证。" },
        { value: 2, label: "方案优化", description: "根据打分报告重构并锁定最终的常量设定墙。" }
      ]
    },
    {
      value: 2,
      label: "阶段三：【环节循环】",
      description: "执行『多智能体概念博弈 -> QA量化裁判 -> 电影级剧本落地』的全套自愈闭环。",
      is_loop: true,
      loop_range: [3, 9],
      start_loop_x_options: [
        { value: 3, label: "环节一", cn_num: "一" },
        { value: 4, label: "环节二", cn_num: "二" },
        { value: 5, label: "环节三", cn_num: "三" },
        { value: 6, label: "环节四", cn_num: "四" },
        { value: 7, label: "环节五", cn_num: "五" },
        { value: 8, label: "环节六", cn_num: "六" },
        { value: 9, label: "环节七", cn_num: "七" }
      ],
      start_step_index_options: [
        { value: 0, label: "正方发散", description: "基于降维备选锁，提出 5 个切合该环节主题的备选方案。" },
        { value: 1, label: "正方收敛", description: "正方选出 1 个最优方案并细化为环节大纲。" },
        { value: 2, label: "反方硬核发散", description: "基于硬核极客维度，再提出 5 个颠覆性备选方案。" },
        { value: 3, label: "反方收敛", description: "反方选出 1 个最优硬核方案。" },
        { value: 4, label: "QA大纲裁判", description: "执行10维量化打分（各10分），输出决胜方案并明确修改点。" },
        { value: 5, label: "大纲隔离区修复", description: "吸收裁判意见，重构出该环节无漏洞完美大纲。" },
        { value: 6, label: "导演级剧本扩写", description: "将大纲转化为带有逐字稿原话、具体物理动作、排版防护的详细剧本。" },
        { value: 7, label: "剧本10维蓝军刺探", description: "刺探剧本是否泄漏黑话术语、是否带有概括性废话或遗失前端排版标签。" },
        { value: 8, label: "终极剧本修复", description: "基于蓝军刺探报告，全量输出该环节不可挑剔的最终剧本。" }
      ]
    },
    {
      value: 3,
      label: "阶段四：拼装五段教案",
      description: "采用物理切割手段，将所有已定稿的碎片教案按九大板块标准重组为完整文档。",
      start_step_index_options: [
        { value: 0, label: "载入教案模板框架", description: "在第一部分生成时挂载，提供全局排版骨架。" },
        { value: 1, label: "执行五段输出协议", description: "在分 5 次生成时连续挂载，强制定死每一次的输出边界，防止大模型爆显存截断。" }
      ]
    },
    {
      value: 4,
      label: "阶段五：终极全域QA审判",
      description: "对全篇教案执行最高规格的雷达扫描与等级评定，验证全链路一致性。",
      start_step_index_options: []
    }
  ]
};

export const runCurriculumPipeline = (
  onMessage: (message: CurriculumPipelineProgressMessage) => void,
  onOpen?: () => void
) => {
  const token = getToken();
  if (!token) throw new Error('Authentication token not found');

  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const wsUrl = `${protocol}//${getWsHost()}/api/v1/curriculum-pipeline/run?token=${token}`;
  
  const ws = new WebSocket(wsUrl);
  
  ws.onopen = () => {
    console.log('🚀 [CurriculumPipeline] WebSocket connected');
    if (onOpen) onOpen();
  };
  
  ws.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data);
      onMessage(data);
    } catch (error) {
      onMessage({ type: 'error', message: 'Failed to parse message' });
    }
  };
  
  ws.onerror = (e) => {
    console.error('🚀 [CurriculumPipeline] WebSocket error:', e);
    onMessage({ type: 'error', message: 'WebSocket connection error' });
  };
  
  ws.onclose = (e) => {
    console.log('🚀 [CurriculumPipeline] WebSocket closed:', e.code, e.reason);
    if (!e.wasClean && e.code !== 1000) {
      onMessage({ type: 'error', message: `Connection closed: ${e.reason || 'Unknown reason'}` });
    }
  };

  return {
    start: (prompt: string, fileUrls?: string[], startPhaseIndex?: number, startLoopX?: number, startStepIndex?: number) => {
      const msg: CurriculumPipelineStartMessage = { prompt };
      if (fileUrls && fileUrls.length > 0) {
        msg.file_urls = fileUrls;
      }
      if (startPhaseIndex !== undefined) {
        msg.start_phase_index = startPhaseIndex;
      }
      if (startLoopX !== undefined) {
        msg.start_loop_x = startLoopX;
      }
      if (startStepIndex !== undefined) {
        msg.start_step_index = startStepIndex;
      }
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify(msg));
      } else {
        console.error('🚀 [CurriculumPipeline] WebSocket not open, readyState:', ws.readyState);
      }
    },
    close: () => ws.close(),
    getReadyState: () => ws.readyState,
    READY_STATE: {
      CONNECTING: WebSocket.CONNECTING,
      OPEN: WebSocket.OPEN,
      CLOSING: WebSocket.CLOSING,
      CLOSED: WebSocket.CLOSED
    }
  };
};